import type { ArticleRef } from "@ellaandil/formatters";
import { ArrowRightAlt } from "@ellaandil/ui";
import { cva, type VariantProps } from "class-variance-authority";
import Image from "next/image";
import { type ComponentProps, forwardRef } from "react";
import { twMerge } from "tailwind-merge";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Skeleton } from "@/components/ui/Skeleton";
import { Typography } from "@/components/ui/Typography";
import { Link } from "@/i18n/routing";
import { sbLink } from "@/lib/storyblok/link";

const cardStyles = cva("flex w-full flex-col items-start", {
	variants: {
		size: {
			small: "gap-grid-component-spacing-2xs",
			medium: "gap-2",
			large: "gap-4",
		},
	},
});

const imageStyles = cva("relative w-full overflow-hidden bg-background-bg-image", {
	variants: {
		variant: {
			portrait: "",
			landscape: "aspect-[3/2]",
			square: "aspect-square",
			"horizontal-highlight": "",
		},
		size: { small: "", medium: "", large: "" },
	},
	compoundVariants: [
		{ variant: "portrait", size: "small", className: "aspect-[3/4]" },
		{ variant: "portrait", size: "medium", className: "aspect-[160/213]" },
		{ variant: "portrait", size: "large", className: "aspect-[3/4]" },
	],
});

const titleStyles = cva("w-full font-display font-light leading-[1.25] text-text-dark-text-1", {
	variants: {
		size: {
			small: "text-xs",
			medium: "text-base",
			large: "text-base",
		},
	},
});

const descriptionStyles = cva("w-full font-body text-text-dark-text-1", {
	variants: {
		size: {
			small: "text-[10px] leading-[1.5]",
			medium: "text-xs leading-[1.5]",
			large: "text-xs leading-[1.5]",
		},
	},
});

const textBlockGap = cva("flex w-full flex-col items-start", {
	variants: {
		size: {
			small: "gap-2",
			medium: "gap-3",
			large: "gap-3",
		},
	},
});

type CardVariants = VariantProps<typeof cardStyles> & VariantProps<typeof imageStyles>;

export type ArticleCardVariant = NonNullable<CardVariants["variant"]>;
export type ArticleCardSize = NonNullable<CardVariants["size"]>;

export type ArticleCardProps = {
	variant?: ArticleCardVariant;
	size?: ArticleCardSize;
	sizes?: string;
} & ComponentProps<"article"> &
	ArticleRef;

export const ArticleCard = forwardRef<HTMLDivElement, ArticleCardProps>(
	(
		{
			variant = "portrait",
			size = "medium",
			image,
			tags,
			title,
			description,
			slug,
			label,
			sizes = "(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw",
			...props
		},
		ref,
	) => {
		const showText = size !== "small" || Boolean(title || description);
		const href = sbLink(slug)?.href ?? slug;

		if (variant === "horizontal-highlight") {
			return (
				<Link href={href} style={{ display: "contents" }}>
					<article
						ref={ref}
						{...props}
						className={twMerge(
							"relative flex aspect-[4/3] w-full items-end overflow-hidden bg-background-bg-image",
							props.className,
						)}
					>
						{image?.filename && (
							<Image
								src={image.filename}
								alt={image.alt ?? ""}
								fill
								sizes={sizes}
								className="pointer-events-none object-cover"
							/>
						)}
						<div aria-hidden className="absolute inset-0 bg-overlay-2" />
						<div className="relative flex w-full items-end gap-grid-component-spacing-4xs p-grid-component-spacing-2xs">
							{description && (
								<p className="min-w-0 flex-1 font-body text-xs leading-[1.5] text-text-sand-text-2">
									{description}
								</p>
							)}
							{label && (
								<Button
									as={Link}
									href={href}
									variant="solid"
									color="white"
									size="medium"
									rightIcon={ArrowRightAlt}
								>
									{label}
								</Button>
							)}
						</div>
					</article>
				</Link>
			);
		}

		return (
			<Link href={href} style={{ display: "contents" }}>
				<article ref={ref} {...props} className={twMerge(cardStyles({ size }), props.className)}>
					<div className={imageStyles({ variant, size })}>
						{image?.filename && (
							<Image
								src={image.filename}
								alt={image.alt ?? ""}
								fill
								sizes={sizes}
								className="object-cover"
							/>
						)}
						{tags && tags.length > 0 && (
							<div className="absolute inset-x-0 top-0 flex flex-wrap items-start gap-1 p-grid-component-spacing-2xs">
								{tags.map((label) => (
									<Badge key={label} color="sand" size="small">
										{label}
									</Badge>
								))}
							</div>
						)}
					</div>
					{showText && (title || description) && (
						<div className="flex w-full flex-col items-start gap-grid-component-spacing-2xs">
							{(title || description) && (
								<div className={textBlockGap({ size })}>
									{title && <p className={titleStyles({ size })}>{title}</p>}
									{description && <p className={descriptionStyles({ size })}>{description}</p>}
								</div>
							)}
							{label && (
								<Typography
									variant="body-xs-reg"
									as="span"
									className={twMerge(
										"underline decoration-solid underline-offset-[0.2em]",
										size === "small" && "text-[10px]",
									)}
								>
									{label}
								</Typography>
							)}
						</div>
					)}
				</article>
			</Link>
		);
	},
);

ArticleCard.displayName = "ArticleCard";

export type ArticleCardSkeletonProps = {
	variant?: ArticleCardVariant;
	size?: ArticleCardSize;
} & ComponentProps<"article">;

export const ArticleCardSkeleton = forwardRef<HTMLDivElement, ArticleCardSkeletonProps>(
	({ variant = "portrait", size = "medium", ...props }, ref) => {
		return (
			<article ref={ref} {...props} className={cardStyles({ size })}>
				<div className={imageStyles({ variant, size })}>
					<Skeleton className="w-full h-full" />
				</div>
				<div className="flex w-full flex-col items-start gap-grid-component-spacing-2xs">
					<div className={textBlockGap({ size })}>
						<Skeleton fitContent>
							<p className={titleStyles({ size })}>Lorem, ipsum dolor.</p>
						</Skeleton>
						<Skeleton fitContent>
							<p className={descriptionStyles({ size })}>
								Lorem ipsum dolor sit amet, consectetur adipisicing elit. Expedita, possimus.
							</p>
						</Skeleton>
					</div>
					<Skeleton fitContent>
						<Typography
							variant="body-xs-reg"
							as="span"
							className="underline decoration-solid underline-offset-[0.2em]"
						>
							Label text
						</Typography>
					</Skeleton>
				</div>
			</article>
		);
	},
);

ArticleCardSkeleton.displayName = "ArticleCardSkeleton";
